import { IconSelectClean } from "@components/inputs/select/IconSelect";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { IFieldInfoProperties, IGetValueArgs, TGetValueFn, TTemporalDialogSettings } from "@components/smart/FieldInfo";
import { ISmartFieldBlur } from "@components/smart/smartField/SmartField";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import {
    EntityTypeName,
    PrBenefitEntity,
    PrBenefitTemporalCurrentEntity,
    PrBenefitTemporalEntity,
    PrBenefitTypeEntity,
    PrEmploymentBenefitEntity,
    PrEmploymentBenefitTemporalEntity,
    PrEmploymentEntity,
    PrEmploymentTemplateBenefitEntity,
    PrEmploymentTemplateBenefitTemporalEntity,
    PrEmploymentTemplateEntity,
    PrOtherBenefitCategoryEntity,
    PrOtherBenefitTaxAndContributionsApplicabilityEntity,
    PrOtherBenefitTaxDeducibilityEntity,
    PrPayslipEmploymentBenefitEntity,
    TemporalEntityEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrBenefitTypeCode,
    PrOtherBenefitCategoryCode,
    PrOtherBenefitTaxAndContributionsApplicabilityCode,
    PrOtherBenefitTaxDeducibilityCode
} from "@odata/GeneratedEnums";
import { getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import {
    getFakeTemporalFieldFormatter,
    getSyncedLineItemHistoryValues,
    getTemporalDataColumns,
    getTemporalPropertyExtraFieldContentAfter,
    getTemporalPropertyLineItemFieldDefinition,
    IGetTemporalPropertyLineItemFieldDefinitionArgs,
    isFieldSynchronized,
    setTemporalCurrentValue,
    TemporalEntityProp
} from "@odata/TemporalUtils";
import { TFieldsDefinition } from "@pages/PageUtils";
import { getPayrollCurrencyFormatter, getPayrollCurrencyUnit } from "@pages/payroll/payroll.utils";
import { getCompanyCurrencyUnit } from "@utils/CompanyUtils";
import i18next from "i18next";
import React from "react";

import { DASH_CHARACTER } from "../../../constants";
import {
    BasicInputSizes,
    FastEntryInputSizes,
    FieldType,
    GroupedField,
    QueryParam,
    ToolbarItemType,
    ValidatorType
} from "../../../enums";
import { pickPairBy } from "../../../global.utils";
import { TableStorage } from "../../../model/TableStorage";
import { getQueryParameters } from "../../../routes/Routes.utils";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ICustomButtonDefArgs, TableActionOrder, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";

export const BENEFIT_ADD_BUTTON = "benefitAddButton";

// Fake fields for SmartTemporalPropertyDialog to display non-temporary values in it
export const BenefitFakeTypePath = BindingContext.localContext("Type");

export enum BenefitStatus {
    Active = "Active",
    Planned = "Planned",
    Finished = "Finished",
}

export const BenefitTypeOrder: PrBenefitTypeCode[] = [
    PrBenefitTypeCode.MealVouchers,
    PrBenefitTypeCode.LumpSumMealBenefit,
    PrBenefitTypeCode.RemoteWorkBenefit,
    PrBenefitTypeCode.OtherBenefit
];

function getIconForBenefitType(type: PrBenefitTypeCode): string {
    switch (type) {
        case PrBenefitTypeCode.OtherBenefit:
            return "OtherBenefit";
        case PrBenefitTypeCode.MealVouchers:
            return "MealVoucher";
        case PrBenefitTypeCode.LumpSumMealBenefit:
            return "LumpSumMealBenefit";
        case PrBenefitTypeCode.RemoteWorkBenefit:
            return "RemoteWorkBenefit";
    }
}

function sortByBenefitTypeOrder(a: ISelectItem, b: ISelectItem): number {
    const indexA = BenefitTypeOrder.indexOf(a.id as PrBenefitTypeCode);
    const indexB = BenefitTypeOrder.indexOf(b.id as PrBenefitTypeCode);
    return (indexA === -1 ? Number.MAX_VALUE : indexA) - (indexB === -1 ? Number.MAX_VALUE : indexB);
}

export function getToolbarAddButtonConfig(storage: TableStorage, ref: React.RefObject<IconSelectClean>, args?: ICustomButtonDefArgs): TTableToolbarItem {
    const items = getEnumSelectItems(EntityTypeName.PrBenefitType)
        .sort(sortByBenefitTypeOrder)
        .map(item => ({
            ...item,
            iconName: getIconForBenefitType(item.id as PrBenefitTypeCode)
        }));

    return {
        id: BENEFIT_ADD_BUTTON,
        itemType: ToolbarItemType.IconSelect,
        iconName: "Add",
        label: i18next.t("Benefit:NewBenefit"),
        order: TableActionOrder.Add,
        itemProps: {
            isTransparent: true,
            selectProps: {
                isActive: storage.data.addingRow || args?.isActive,
                isDisabled: !!storage.tableAction || args?.isDisabled,
                ref
            },
            items
        }
    };
}

export function recalculateBenefitMealVoucherAmounts(storage: FormStorage, args: ISmartFieldBlur, suffix = ""): void {
    const { bindingContext } = args;
    const fullPath = bindingContext.getFullPath();
    if (!fullPath.includes(PrEmploymentEntity.Benefits) && !fullPath.includes(EntityTypeName.PrBenefit)) {
        // not benefit change / blur
        return;
    }

    const value = storage.getValue(bindingContext);
    const path = bindingContext.getPath() as PrBenefitTemporalCurrentEntity;
    const amountPaths = [
        PrBenefitTemporalCurrentEntity.Amount,
        PrBenefitTemporalCurrentEntity.AmountEmployer,
        PrBenefitTemporalCurrentEntity.AmountEmployee
    ].map(p => p + suffix);
    // Paths enhanced with suffix
    const [amountPath, amountEmployerPath, amountEmployeePath] = amountPaths;
    if (isNaN(value) || value < 0 || !amountPaths.includes(path) || !isMealVoucher({ storage, bindingContext })) {
        // we don't recalculate in such cases
        return;
    }
    const parentBc = bindingContext.getParent();
    const isTemporalCurrent = TemporalEntityProp.CurrentTemporalPropertyBag === parentBc.getPath(true);
    const employeeShareBc = parentBc.navigate(amountEmployeePath);
    const employerShareBc = parentBc.navigate(amountEmployerPath);

    if (path === amountPath) {
        const employerShare = storage.getValue(employerShareBc);
        const employeeShare = storage.getValue(employeeShareBc);
        const previousTotal = employeeShare + employerShare;
        const share = previousTotal ? employeeShare / previousTotal : 0.45;
        const employeeShareNew = Math.round(value * share);
        if (isTemporalCurrent) {
            setTemporalCurrentValue(storage, employeeShareBc, employeeShareNew);
            setTemporalCurrentValue(storage, employerShareBc, value - employeeShareNew);
        } else {
            storage.setValue(employeeShareBc, employeeShareNew);
            storage.setValue(employerShareBc, value - employeeShareNew);
        }
    } else {
        // just recalculate the other from total if set
        const total = storage.getValue(parentBc.navigate(amountPath));
        if (isNaN(total) || value > total || value < 0) {
            return;
        }
        const isEmployee = path === amountEmployeePath;
        const [recalcBc, refreshBc] = pickPairBy(isEmployee, employerShareBc, employeeShareBc);
        if (isTemporalCurrent) {
            setTemporalCurrentValue(storage, recalcBc, total - value);
        } else {
            storage.setValue(recalcBc, total - value);
        }
        storage.addActiveField(refreshBc);
    }
}

export const areAllBenefitsOfSameType = (args: IGetValueArgs): boolean => {
    const { storage, bindingContext } = args;
    const benefits = storage.getValue(bindingContext) ?? [];
    if (benefits.length < 2) {
        return true;
    }
    const _getProp = (e: IEntity) => e?.Type?.Code;
    const refType = _getProp(benefits[0]);
    return !benefits.slice(1).find((sc: IEntity) => _getProp(sc) !== refType);
};

/** Returns filter string for the given tab ID */
export function getFilterForTabId(tabId: string): string {
    const nowDate = getUtcDayjs().startOf("day").toISOString().substring(0, 10);
    const dateToPath = createPath(PrBenefitEntity.CurrentTemporalPropertyBag, PrBenefitTemporalCurrentEntity.DateValidTo);
    switch (tabId as BenefitStatus) {
        case BenefitStatus.Active:
            return `${dateToPath} gt ${nowDate} or ${dateToPath} eq null`;
        case BenefitStatus.Finished:
            return `${dateToPath} lt ${nowDate}`;
    }
    return "";
}

/**
 * Method could be used generally at Benefit form, but also at EmploymentTemplate and Employment forms,
 * use relative bc
 * @param type
 */
export function isOfType(type: PrBenefitTypeCode): TGetValueFn<boolean> {
    return (args) => {
        const { storage, bindingContext } = args;
        // used in different contexts (employment, employment template, benefit or payslip), but parentBc should be always the benefit
        let parentBc = bindingContext.getParent();
        if (parentBc.getPath(true) === TemporalEntityProp.TemporalPropertyBag) {
            // in temporal dialog, SmartFastEntryList use temporal property bag Bc,
            // but we need to check only current value
            parentBc = parentBc.getParent().navigate(TemporalEntityProp.CurrentTemporalPropertyBag);
        }
        const benefitBc = parentBc.getPath(true) === "Benefits" ? parentBc : parentBc.getParent();
        let refCode: PrBenefitTypeCode;
        if (benefitBc.isCollection() && !benefitBc.getKey()) {
            const component = args.storage.getValue(benefitBc)?.[0];
            refCode = component?.Type?.Code;
        } else {
            const component = storage.getValue(benefitBc);
            refCode = component?.Type?.Code as PrBenefitTypeCode;
        }
        return !refCode || refCode === type;
    };
}

export const isMealVoucher = isOfType(PrBenefitTypeCode.MealVouchers);
export const isOtherBenefit = isOfType(PrBenefitTypeCode.OtherBenefit);

export function getMealVoucherShareUnitFormatter(prop: PrBenefitTemporalCurrentEntity.Amount | PrPayslipEmploymentBenefitEntity.AmountOverride): TGetValueFn<string> {
    return (args: IGetValueArgs) => {
        const currency = getPayrollCurrencyUnit()(args);
        const { storage, bindingContext } = args;
        const amountBc = bindingContext.getParent().navigate(prop);
        const value = storage.getValue(bindingContext);
        const fullAmount = storage.getValue(amountBc);
        let percentage: number;
        if (!isNaN(value) && fullAmount && fullAmount >= value) {
            percentage = Math.round(100 * value / fullAmount);
        }
        return `${currency} | ${percentage ? `${percentage} %` : DASH_CHARACTER}`;
    };
}
const mealVoucherShareUnitFormatter = getMealVoucherShareUnitFormatter(PrBenefitTemporalCurrentEntity.Amount);

const getBenefitAmountUnit: TGetValueFn<string> = (args: IGetValueArgs) => {
    const currency = getCompanyCurrencyUnit(args);
    let unit = "";
    if (isOfType(PrBenefitTypeCode.LumpSumMealBenefit)(args)) {
        unit = i18next.t("Common:General.Day"); // per day
    } else if (isOfType(PrBenefitTypeCode.RemoteWorkBenefit)(args)) {
        unit = i18next.t("Common:General.Hour"); // per hour
    }
    return `${currency}${unit ? `/${unit}` : ""}`;
};

export function getBenefitFieldDefinition(propName: string, temporalDialogSettings?: TTemporalDialogSettings): Partial<IFieldInfoProperties> {
    let def: Partial<IFieldInfoProperties> = {};
    switch(propName) {
        case BenefitFakeTypePath:
            def = {
                width: BasicInputSizes.M,
                label: i18next.t("Benefit:Form.Type"),
                isReadOnly: true,
                customizationData: {
                    useForCustomization: false
                },
                formatter: getFakeTemporalFieldFormatter(PrBenefitEntity.Type)
            };
            break;
        case PrBenefitTemporalEntity.Name:
            def = {
                width: BasicInputSizes.L,
                isRequired: true,
            };
            break;
        case PrBenefitEntity.Type:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: (args: IGetValueArgs) => {
                    const key = getQueryParameters()[QueryParam.Type];
                    if (key && Object.values(PrBenefitTypeCode).includes(key as PrBenefitTypeCode)) {
                        return key;
                    }
                    return PrBenefitTypeCode.OtherBenefit;
                },
                fieldSettings: {
                    displayName: PrBenefitTypeEntity.Name,
                    transformFetchedItems: items =>
                        items.sort(sortByBenefitTypeOrder)
                },
                isRequired: true
            };
            break;
        case PrBenefitTemporalEntity.Amount:
            def = {
                label: i18next.t("Benefit:Form.Amount"),
                width: BasicInputSizes.S,
                isRequired: true,
                formatter: getPayrollCurrencyFormatter(),
                fieldSettings: {
                    unit: getBenefitAmountUnit
                },
                validator: {
                    type: ValidatorType.PositiveNumber
                }
            };
            break;
        case PrBenefitTemporalEntity.AmountEmployee:
            def = {
                label: i18next.t("Benefit:Form.EmployeeAmount"),
                type: FieldType.NumberInput,
                width: BasicInputSizes.M,
                isRequired: true,
                isVisible: isMealVoucher,
                formatter: getPayrollCurrencyFormatter(),
                fieldSettings: {
                    unit: mealVoucherShareUnitFormatter
                }
            };
            break;
        case PrBenefitTemporalEntity.AmountEmployer:
            def = {
                label: i18next.t("Benefit:Form.EmployerAmount"),
                type: FieldType.NumberInput,
                width: BasicInputSizes.M,
                isRequired: true,
                isVisible: isMealVoucher,
                formatter: getPayrollCurrencyFormatter(),
                fieldSettings: {
                    unit: mealVoucherShareUnitFormatter
                }
            };
            break;
        case PrBenefitTemporalEntity.OtherCategory:
            def = {
                type: FieldType.ComboBox,
                width: FastEntryInputSizes.S,
                isRequired: true,
                defaultValue: PrOtherBenefitCategoryCode.Financial,
                isVisible: isOtherBenefit,
                fieldSettings: {
                    displayName: PrOtherBenefitCategoryEntity.Name
                }
            };
            break;
        case PrBenefitTemporalEntity.TaxAndContributionsApplicability:
            def = {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                isRequired: true,
                defaultValue: PrOtherBenefitTaxAndContributionsApplicabilityCode.FullyExempt,
                isVisible: isOtherBenefit,
                fieldSettings: {
                    displayName: PrOtherBenefitTaxAndContributionsApplicabilityEntity.Name
                }
            };
            break;
        case PrBenefitTemporalEntity.TaxDeducibility:
            def = {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                isRequired: true,
                defaultValue: PrOtherBenefitTaxDeducibilityCode.Nondeducible,
                isVisible: isOtherBenefit,
                fieldSettings: {
                    displayName: PrOtherBenefitTaxDeducibilityEntity.Name
                }
            };
            break;
    }

    return temporalDialogSettings ? {
        ...def,
        fieldSettings: {
            ...def.fieldSettings,
            temporalDialog: temporalDialogSettings
        }
    } : def;
}

export enum BenefitVariant {
    Base = "Base",
    Employment = "Employment",
    EmploymentTemplate = "EmploymentTemplate"
}


export function getBenefitColumns(type: BenefitVariant = BenefitVariant.Base): string[] {
    const BenefitTemporalEntity = type === BenefitVariant.Employment ? PrEmploymentBenefitTemporalEntity
            : type === BenefitVariant.EmploymentTemplate ? PrEmploymentTemplateBenefitTemporalEntity : PrBenefitTemporalEntity;

    return [
        BenefitTemporalEntity.Name,
        BenefitFakeTypePath,
        BenefitTemporalEntity.OtherCategory,
        BenefitTemporalEntity.TaxAndContributionsApplicability,
        BenefitTemporalEntity.TaxDeducibility,
        BenefitTemporalEntity.Amount,
        BenefitTemporalEntity.AmountEmployer,
        BenefitTemporalEntity.AmountEmployee,
    ];
}

export const getBenefitTemporalDialogSettings = (type: BenefitVariant = BenefitVariant.Base, withHistory = true): TTemporalDialogSettings => {
    return {
        dialogTitle: i18next.t("Benefit:Form.PlanChange"),
        columns: getBenefitColumns(type),
        extractHistory: withHistory ? getSyncedLineItemHistoryValues : undefined,
    };
};

export function getBenefitFieldDefinitions(type: BenefitVariant.Employment | BenefitVariant.EmploymentTemplate = BenefitVariant.Employment): TFieldsDefinition {
    const isEmployment = type === BenefitVariant.Employment;

    const MainEntity = isEmployment ? PrEmploymentEntity : PrEmploymentTemplateEntity;
    const BenefitEntity = isEmployment ? PrEmploymentBenefitEntity : PrEmploymentTemplateBenefitEntity;
    const BenefitTemporalEntity = isEmployment ? PrEmploymentBenefitTemporalEntity : PrEmploymentTemplateBenefitTemporalEntity;

    const temporalDialogSettings: TTemporalDialogSettings = getBenefitTemporalDialogSettings(type, true);

    const commonDefs: Omit<IGetTemporalPropertyLineItemFieldDefinitionArgs, "propName"> = {
        collectionName: MainEntity.Benefits,
        withOpeningButton: false,
        isSynchronized: true
    };

    const columns = getBenefitColumns(type);
    const dataColumns = getTemporalDataColumns(columns);

    // additional properties, which are attached to the amount field
    const additionalProperties =
            dataColumns.map(id => ({ id: `/${MainEntity.Benefits}/${PrBenefitEntity.TemporalPropertyBag}/${id}` }));

    [TemporalEntityEntity.DateValidFrom, TemporalEntityEntity.DateValidTo].forEach(id => {
        additionalProperties.push({ id: `/${createPath(MainEntity.Benefits, PrBenefitEntity.CurrentTemporalPropertyBag, id)}`});
    });

    dataColumns.forEach(id => {
        additionalProperties.push({ id: `/${createPath(MainEntity.Benefits, BenefitEntity.Default, PrBenefitEntity.CurrentTemporalPropertyBag, id)}`});
        additionalProperties.push({ id: `/${createPath(MainEntity.Benefits, BenefitEntity.Default, PrBenefitEntity.TemporalPropertyBag, id)}`});
    });

    const isExistingLineItem = (args: IGetValueArgs) => {
        return !args.bindingContext.getParent().isNew();
    };

    const TypeDef = getBenefitFieldDefinition(BenefitEntity.Type);

    return {
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: BenefitTemporalEntity.Name,
            definition: {
                ...getBenefitFieldDefinition(BenefitTemporalEntity.Name, temporalDialogSettings),
                additionalProperties,
                groupedField: GroupedField.WrappedMultiStart
            },
        }),
        [createPath(MainEntity.Benefits, BenefitEntity.Type)]: {
            ...TypeDef,
            isReadOnly: (args: IGetValueArgs) => isExistingLineItem(args) || isFieldSynchronized(args),
            fieldSettings: {
                ...TypeDef.fieldSettings,
                isSynchronized: isFieldSynchronized
            },
        },
        [createPath(MainEntity.Benefits, BenefitEntity.TemporalPropertyBag, BenefitFakeTypePath)]: getBenefitFieldDefinition(BenefitFakeTypePath),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: BenefitTemporalEntity.OtherCategory,
            definition: {
                ...getBenefitFieldDefinition(BenefitTemporalEntity.OtherCategory, temporalDialogSettings),
            },
            withOpeningButton: false
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: BenefitTemporalEntity.TaxAndContributionsApplicability,
            definition: {
                ...getBenefitFieldDefinition(BenefitTemporalEntity.TaxAndContributionsApplicability, temporalDialogSettings),
            },
            withOpeningButton: false
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: BenefitTemporalEntity.TaxDeducibility,
            definition: {
                ...getBenefitFieldDefinition(BenefitTemporalEntity.TaxDeducibility, temporalDialogSettings),
            },
            withOpeningButton: false
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: BenefitTemporalEntity.Amount,
            definition: {
                ...getBenefitFieldDefinition(BenefitTemporalEntity.Amount, temporalDialogSettings)
            },
            currentDefinition: {
                groupedField: (args => isMealVoucher(args) ? null : GroupedField.WrappedMultiEnd),
                extraFieldContentAfter: (args) =>
                        isMealVoucher(args) ? null : getTemporalPropertyExtraFieldContentAfter()(args)
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: BenefitTemporalEntity.AmountEmployer,
            definition: {
                ...getBenefitFieldDefinition(BenefitTemporalEntity.AmountEmployer, temporalDialogSettings),
            },
            withOpeningButton: false
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: BenefitTemporalEntity.AmountEmployee,
            definition: {
                ...getBenefitFieldDefinition(BenefitTemporalEntity.AmountEmployee, temporalDialogSettings),
                groupedField: GroupedField.WrappedMultiEnd,
            },
            withOpeningButton: true
        }),
    };
}